<!-- eslint-disable max-len -->
<template>
  <div>
    <Card title="Payout Dashboard">
      <template v-slot:body>
        <div>
          <div class="pb-6 flex flex-col md:flex-row gap-4">
            <div class="flex-1 rounded border border-gray-200 bg-gray-100 p-6">
              <p class="text-xs uppercase font-bold text-gray-500">
                Wise Balance
              </p>
              <p class="pt-2 text-3xl font-bold text-gray-800">
                {{ wiseBalanceText }}
              </p>
              <div class="pt-3">
                <VueCustomTooltip
                  label="Refresh balance"
                  :position="'is-bottom'"
                >
                  <button
                    @click="refreshWiseBalance"
                    class="p-2 w-8 rounded bg-blue-400 hover:bg-blue-500 focus:outline-none"
                  >
                    <font-awesome-icon
                      class="text-white"
                      :icon="['fa', 'sync']"
                    />
                  </button>
                </VueCustomTooltip>
              </div>
            </div>
            <div class="flex-1 rounded border border-gray-200 bg-gray-100 p-6">
              <p class="text-xs uppercase font-bold text-gray-500">
                Payout Value
              </p>
              <p
                class="pt-2 text-3xl font-bold"
                :class="hasInsufficientFunds ? 'text-red-600' : 'text-blue-700'"
              >
                {{ accumulatedValueText }}
              </p>
            </div>
          </div>
          <div class="pb-6 flex justify-between items-center">
            <div class="flex items-center">
              <div class="mr-2">
                <form-item label="From" no-margin>
                  <template v-slot>
                    <el-date-picker
                      v-model="startMonth"
                      type="month"
                      :clearable="false"
                      placeholder="From month"
                      style="width: 100%"
                      @change="onChangeStartDate"
                    >
                    </el-date-picker>
                  </template>
                </form-item>
              </div>
              <div class="mr-2">
                <form-item label="To" no-margin>
                  <template v-slot>
                    <el-date-picker
                      v-model="endMonth"
                      type="month"
                      :clearable="false"
                      placeholder="To month"
                      style="width: 100%"
                      @change="onChangeEndDate"
                    >
                    </el-date-picker>
                  </template>
                </form-item>
              </div>
              <div class="relative self-stretch">
                <div
                  class="flex"
                  style="position: absolute; bottom: 0; width: max-content; "
                >
                  <Button
                    variant="primary"
                    :label="generateReportLabel"
                    :disabled="tickedCount === 0"
                    @clicked="generateReport"
                  />
                </div>
              </div>
            </div>
          </div>
          <div v-if="pendingReview" class="pb-6">
            <div
              class="flex items-start rounded border border-yellow-300 bg-yellow-50 p-4"
            >
              <span
                class="flex items-center justify-center w-6 h-6 mr-3 rounded-full bg-yellow-500 text-white text-sm font-bold flex-shrink-0"
                >!</span
              >
              <div>
                <p class="font-bold text-gray-800">
                  Submitted for review — table locked
                </p>
                <p class="pt-1 text-sm text-gray-600">
                  {{ pendingReviewBannerText }}
                </p>
                <div class="pt-3">
                  <VueCustomTooltip
                    :label="ownerReviewButtonTooltip"
                    :position="'is-bottom'"
                  >
                    <div>
                      <Button
                        variant="primary"
                        label="Open payout review"
                        @clicked="openOwnerReview"
                      />
                    </div>
                  </VueCustomTooltip>
                </div>
              </div>
            </div>
          </div>
          <div v-if="missingMonths.length > 0" class="pb-4">
            <p class="text-xs uppercase font-bold text-yellow-600">
              No payouts: {{ missingMonths }}
            </p>
          </div>
          <data-tables-server
            ref="tableTransaction"
            :data="data"
            :total="total"
            :current-page.sync="page"
            :page-size.sync="pageSize"
            :pagination-props="{
              background: true,
              pageSizes: [displayingItem]
            }"
            @query-change="onChangeTable"
          >
            <el-table-column
              label="Artist Name"
              prop="artist.name"
              min-width="200px"
            >
              <template slot-scope="scope">
                <div class="flex items-center">
                  <font-awesome-icon
                    v-if="isTicked(scope.row)"
                    class="text-green-500 mr-2"
                    :icon="['fa', 'check']"
                  />
                  <span>{{ scope.row.artist.name }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column
              label="Email"
              prop="artist.email"
              min-width="200px"
            >
              <template slot-scope="scope">
                <span class="whitespace-no-wrap">{{
                  scope.row.artist.email
                }}</span>
              </template>
            </el-table-column>
            <el-table-column
              v-for="title in titles"
              :prop="title.prop"
              :label="title.label"
              :key="title.prop"
            >
            </el-table-column>
            <el-table-column label="Months" min-width="200px">
              <template slot-scope="scope">
                <span>{{ scope.row.months_text }}</span>
              </template>
            </el-table-column>
            <el-table-column label="Bank/Wise" min-width="120px">
              <template slot-scope="scope">
                <span>{{ paymentMethodText(scope.row) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="Status" min-width="120px">
              <template slot-scope="scope">
                <span>{{ payoutDisplayStatus(scope.row) }}</span>
              </template>
            </el-table-column>
            <el-table-column align="right" min-width="100px">
              <template slot="header" slot-scope="headerScope">
                <div class="flex items-center justify-end">
                  <el-checkbox
                    :value="isAllTicked"
                    :indeterminate="isIndeterminate"
                    :disabled="tickableRows.length === 0"
                    @change="toggleAll"
                  />
                  <span class="ml-2">Actions</span>
                </div>
              </template>
              <template slot-scope="scope">
                <div class="static inline-block text-left container-action">
                  <VueCustomTooltip label="Detail" :position="'is-left'">
                    <button
                      @click="viewDetail(scope.row)"
                      class="p-2 mr-2 w-8 rounded bg-green-400 hover:bg-green-500 focus:outline-none"
                    >
                      <font-awesome-icon
                        class="text-white"
                        :icon="['fa', 'eye']"
                      />
                    </button>
                  </VueCustomTooltip>
                  <VueCustomTooltip
                    :label="tickDisabledReason(scope.row)"
                    :position="'is-left'"
                  >
                    <el-checkbox
                      class="ml-2"
                      :value="isTicked(scope.row)"
                      :disabled="isPayDisabled(scope.row)"
                      @change="togglePay(scope.row)"
                    />
                  </VueCustomTooltip>
                </div>
              </template>
            </el-table-column>
          </data-tables-server>
          <div class="pt-6 pb-6 flex justify-end">
            <Button
              variant="primary"
              label="Schedule Payout"
              :disabled="tickedCount === 0 || isReviewLocked"
              @clicked="saveMarkPay"
            />
          </div>
          <div class="pt-6">
            <p class="text-xs uppercase font-bold text-gray-500">
              Transaction History
            </p>
            <div class="pt-4">
              <el-table :data="transactionHistory" style="width: 100%">
                <el-table-column
                  label="Period"
                  prop="period"
                  min-width="180px"
                ></el-table-column>
                <el-table-column
                  label="Total"
                  prop="total"
                  min-width="150px"
                ></el-table-column>
                <el-table-column
                  label="Artist"
                  prop="artist_total"
                  min-width="120px"
                ></el-table-column>
                <el-table-column
                  label="Payment Time"
                  prop="payment_time"
                  min-width="180px"
                ></el-table-column>
                <el-table-column label="Status" min-width="120px">
                  <template slot-scope="scope">
                    <span
                      class="px-2 py-1 rounded text-xs font-bold"
                      :class="historyStatusClass(scope.row.status)"
                    >
                      {{ scope.row.status }}
                    </span>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>
        </div>
      </template>
    </Card>
    <ModalDialog
      ref="detailArtist"
      :isShowingModal="isShowingDetailModal"
      :isLoading="isLoading"
      title="Detail Artist"
      labelSubmit="Submit as Transferred"
      @toggleModal="isShowingDetailModal = false"
      @save="makePayment()"
    >
      <template slot="body">
        <div class="py-6">
          <form-item label="Artist Name">
            <template v-slot>
              <p class="text-left font-bold">{{ artist.name }}</p>
            </template>
          </form-item>
          <form-item label="Email">
            <template v-slot>
              <p class="text-left font-bold">{{ artist.email }}</p>
            </template>
          </form-item>
          <form-item label="Wise ID">
            <template v-slot>
              <p class="text-left font-bold">
                {{ wiseIdentifierOf(artist) || "-" }}
              </p>
            </template>
          </form-item>
          <form-item label="Bank">
            <template v-slot>
              <p class="text-left font-bold">
                {{ !!artist.bank ? artist.bank.bank : "-" }}
              </p>
            </template>
          </form-item>
          <form-item label="Account Holder">
            <template v-slot>
              <p class="text-left font-bold">
                {{ !!artist.bank ? artist.bank.account_name : "-" }}
              </p>
            </template>
          </form-item>
          <form-item label="Account Number">
            <template v-slot>
              <p class="text-left font-bold">
                {{ !!artist.bank ? artist.bank.account_number : "-" }}
              </p>
            </template>
          </form-item>
          <form-item label="Swift Code">
            <template v-slot>
              <p class="text-left font-bold">
                {{ !!artist.bank ? artist.bank.swift_code : "-" }}
              </p>
            </template>
          </form-item>
          <form-item label="Amount">
            <template v-slot>
              <p class="text-left font-bold">{{ artist.amount }}</p>
            </template>
          </form-item>
          <form-item label="Paid Amount">
            <div class="inline-flex w-full gap-4 items-center">
              $
              <Input
                v-model="input.amount"
                placeholder="ex. 5"
                :error="
                  $v.input.amount.$dirty &&
                    (!$v.input.amount.required || !$v.input.amount.decimal)
                "
              />
            </div>
            <div class="flex flex-col pt-2">
              <text-error
                v-if="$v.input.amount.$dirty && !$v.input.amount.required"
                text="Please fill Amount."
              />
              <text-error
                v-if="$v.input.amount.$dirty && !$v.input.amount.decimal"
                text="Please fill Amount with number."
              />
            </div>
          </form-item>
          <form-item label="Transfer Date">
            <el-date-picker
              size="large"
              v-model="input.payment_date"
              type="date"
              :clearable="false"
              placeholder="Pick a day"
              style="width: 100%"
            >
            </el-date-picker>
            <div class="flex flex-col pt-2">
              <text-error
                v-if="
                  $v.input.payment_date.$dirty &&
                    !$v.input.payment_date.required
                "
                text="Please fill Payment Date."
              />
            </div>
          </form-item>
        </div>
      </template>
    </ModalDialog>
    <div
      :class="isShowingConfirmModal ? 'z-50' : 'opacity-0 pointer-events-none'"
      class="overflow-x-hidden overflow-y-auto fixed inset-0 top-0 left-0 outline-none focus:outline-none justify-center items-center flex"
    >
      <div class="relative w-auto my-6 mx-auto max-w-md">
        <div
          class="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none"
        >
          <div
            class="flex items-start justify-between p-5 border-b border-solid border-blueGray-200 rounded-t"
          >
            <h3 class="text-xl font-semibold">Confirm schedule payout</h3>
          </div>
          <div class="p-5">
            <p class="text-gray-600">Schedule this payout batch?</p>
            <ul class="pt-3 text-sm text-gray-700">
              <li class="pb-1"><strong>Artists:</strong> {{ tickedCount }}</li>
              <li class="pb-1">
                <strong>Total:</strong> {{ accumulatedValueText }}
              </li>
              <li class="pb-1">
                <strong>Period:</strong> {{ schedulePeriodText }}
              </li>
              <li class="pb-1">
                <strong>Wise balance:</strong> {{ wiseBalanceText }}
              </li>
              <li><strong>After (est.):</strong> {{ balanceAfterText }}</li>
            </ul>
          </div>
          <div
            class="flex items-center justify-end p-6 border-t border-solid border-blueGray-200 rounded-b"
          >
            <button
              type="button"
              class="text-red-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
              @click="closeConfirmModal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="bg-blue-700 text-white active:bg-emerald-600 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
              @click="confirmSchedulePayout"
            >
              Confirm schedule
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="isShowingConfirmModal"
      class="opacity-25 fixed inset-0 z-40 bg-black"
    ></div>
    <div
      :class="isShowingSuccessModal ? 'z-50' : 'opacity-0 pointer-events-none'"
      class="overflow-x-hidden overflow-y-auto fixed inset-0 top-0 left-0 outline-none focus:outline-none justify-center items-center flex"
      role="alertdialog"
      aria-labelledby="successTitle"
      aria-describedby="successNote"
    >
      <div class="relative w-auto my-6 mx-auto max-w-md">
        <div
          class="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none"
        >
          <div class="p-6">
            <div class="flex justify-center pb-4">
              <span
                class="flex items-center justify-center w-12 h-12 rounded-full bg-green-100"
              >
                <svg
                  class="text-green-600 w-6 h-6"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </span>
            </div>
            <h2
              id="successTitle"
              class="text-2xl font-bold text-gray-800 text-center"
            >
              Schedule Successful
            </h2>
            <p class="pt-1 text-sm text-gray-500 text-center">
              The payout batch has been scheduled.
            </p>
            <ul v-if="scheduledSummary" class="pt-4 text-left text-sm">
              <li
                class="flex justify-between items-center py-2 border-b border-gray-200"
              >
                <span class="text-gray-500">Artists</span>
                <strong class="text-gray-800">{{
                  scheduledSummary.artistText
                }}</strong>
              </li>
              <li
                class="flex justify-between items-center py-2 border-b border-gray-200"
              >
                <span class="text-gray-500">Total</span>
                <strong class="text-gray-800">{{
                  scheduledSummary.total
                }}</strong>
              </li>
              <li
                class="flex justify-between items-center py-2 border-b border-gray-200"
              >
                <span class="text-gray-500">Period</span>
                <strong class="text-gray-800">{{
                  scheduledSummary.period
                }}</strong>
              </li>
            </ul>
            <div
              id="successNote"
              class="mt-4 flex items-start p-3 rounded border border-yellow-200 bg-yellow-50"
            >
              <span
                class="flex items-center justify-center w-5 h-5 rounded-full bg-yellow-500 text-white text-xs font-bold"
                >!</span
              >
            </div>
          </div>
          <div
            class="flex items-center justify-center p-6 border-t border-solid border-blueGray-200 rounded-b"
          >
            <button
              type="button"
              class="bg-blue-700 text-white active:bg-emerald-600 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
              @click="closeSuccessModal"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="isShowingSuccessModal"
      class="opacity-25 fixed inset-0 z-40 bg-black"
    ></div>
    <div
      :class="isShowingOwnerModal ? 'z-50' : 'opacity-0 pointer-events-none'"
      class="overflow-x-hidden overflow-y-auto fixed inset-0 top-0 left-0 outline-none focus:outline-none justify-center items-center flex"
      role="dialog"
      aria-labelledby="ownerTitle"
    >
      <div class="relative w-full my-6 mx-auto max-w-3xl px-6">
        <div
          class="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none"
        >
          <div
            class="flex items-center justify-between p-5 border-b border-solid border-blueGray-200 rounded-t bg-blue-700"
          >
            <h3 id="ownerTitle" class="text-xl font-semibold text-white">
              Payout approval — account owner review
            </h3>
            <button
              type="button"
              class="text-white text-2xl leading-none font-bold outline-none focus:outline-none"
              aria-label="Close"
              @click="closeOwnerReview"
            >
              ×
            </button>
          </div>
          <div class="flex flex-col md:flex-row gap-6 p-6">
            <div class="flex-1">
              <p class="text-xs uppercase font-bold text-gray-500 pb-2">
                Artists to be paid
              </p>
              <div
                class="overflow-y-auto max-h-64 rounded border border-gray-200"
              >
                <table class="w-full text-sm">
                  <thead>
                    <tr class="bg-gray-100 text-left">
                      <th class="px-3 py-2">Artist</th>
                      <th class="px-3 py-2 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="artist in pendingReviewArtists"
                      :key="artist.email"
                      class="border-t border-gray-200"
                    >
                      <td class="px-3 py-2">
                        <strong>{{ artist.name }}</strong>
                        <span class="block text-xs text-gray-500">{{
                          artist.email
                        }}</span>
                      </td>
                      <td class="px-3 py-2 text-right whitespace-no-wrap">
                        {{ artistAmountText(artist) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div class="md:w-64">
              <p class="text-xs uppercase font-bold text-gray-500 pb-2">
                Summary
              </p>
              <div class="rounded border border-gray-200 p-4">
                <p class="text-xs text-gray-500">Total amount</p>
                <p class="pb-3 text-2xl font-bold text-gray-800">
                  {{ pendingReviewTotalText }}
                </p>
                <p class="text-xs text-gray-500">Artists to be paid</p>
                <p class="text-base font-bold text-gray-800">
                  {{ pendingReviewCountText }}
                </p>
              </div>
              <div class="flex gap-2 pt-4">
                <button
                  type="button"
                  class="bg-green-600 text-white active:bg-green-700 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                  :disabled="!canReviewPending"
                  @click="reviewPendingPayout('approved')"
                >
                  Approve
                </button>
                <button
                  type="button"
                  class="bg-red-600 text-white active:bg-red-700 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                  :disabled="!canReviewPending"
                  @click="reviewPendingPayout('rejected')"
                >
                  Reject
                </button>
              </div>
              <p v-if="!canReviewPending" class="pt-3 text-xs text-gray-500">
                View only — only the account owner can approve or reject this
                batch.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="isShowingOwnerModal"
      class="opacity-25 fixed inset-0 z-40 bg-black"
    ></div>
  </div>
</template>
<script>
import Vue from "vue";
import { mapActions, mapGetters } from "vuex";
import VueCustomTooltip from "@adamdehaven/vue-custom-tooltip";
import moment from "moment";
import fileDownload from "js-file-download";
import { validationMixin } from "vuelidate";
import { required, decimal } from "vuelidate/lib/validators";
import ModalDialog from "@/components/Common/ModalDialog";
import { Input, FormItem, TextError, Button } from "@/components/Layout/Form";
import Card from "@/components/Layout/Card";
import helperMixins from "../mixins/helpers.mixins";

// the whole range is fetched in one request so payouts can be grouped per artist client-side
const displayingItems = 1000;
// grouped artist rows are paginated client-side
const payoutPageSize = 7;

export default {
  name: "PayoutDashboardPage",
  mixins: [validationMixin, helperMixins],
  data() {
    return {
      isShowingDetailModal: false,
      isShowingConfirmModal: false,
      isShowingSuccessModal: false,
      isShowingOwnerModal: false,
      scheduledSummary: null,
      isLoading: false,
      startMonth: moment().toDate(),
      endMonth: moment().toDate(),
      data: [],
      total: 0,
      groupedData: [],
      page: 1,
      pageSize: payoutPageSize,
      monthsWithData: [],
      displayingItem: payoutPageSize,
      tickedArtists: {},
      titles: [
        {
          prop: "amount_text",
          label: "Amount"
        }
      ],
      artist: {
        name: "",
        email: "",
        bank: {
          bank: "",
          account_name: "",
          account_number: "",
          swift_code: ""
        },
        amount: "",
        created_at: ""
      },
      detailGroup: null,
      filter: {
        page: 1,
        per_page: displayingItems,
        sortby: "id",
        sortorder: "desc",
        start_month: moment().format("YYYY-MM"),
        end_month: moment().format("YYYY-MM")
      },
      input: {
        payment_date: new Date(),
        amount: 0
      }
    };
  },
  validations() {
    return {
      input: {
        amount: {
          required,
          decimal
        },
        payment_date: {
          required
        }
      }
    };
  },
  async mounted() {
    this.toggleDashboardProfile(false);
    await Promise.all([
      this.requestListPayout(this.filter),
      this.requestScheduledPayoutHistory(),
      this.requestPendingPayoutReview(),
      this.requestWiseBalance()
    ]);
    this.restoreTickedPayouts();
    this.maybeOpenOwnerReviewFromLink();
  },
  computed: {
    ...mapGetters("FinanceModule", [
      "listPayout",
      "scheduledPayoutHistory",
      "pendingPayoutReview",
      "wiseBalance"
    ]),
    transactionHistory() {
      return (this.scheduledPayoutHistory.data || []).map(batch => ({
        period: this.formatMonthRange(batch.start_month, batch.end_month),
        // the payment time is the moment the account owner approved the
        // batch; batches that have not been approved yet show Waiting
        payment_time: batch.approved_at
          ? moment(batch.approved_at).format("MMM D, YYYY h:mm A")
          : "Waiting",
        total: `$${this.formatPrice(
          parseFloat(batch.total_amount).toFixed(2)
        )}`,
        artist_total: `${batch.total_artists} ${
          batch.total_artists === 1 ? "artist" : "artists"
        }`,
        status: batch.status
      }));
    },
    tickedCount() {
      return Object.keys(this.tickedArtists).length;
    },
    linkBatchId() {
      const batch = this.$route.query.batch;
      // vue-router gives an array if the param is repeated (?batch=a&batch=b)
      const value = Array.isArray(batch) ? batch[0] : batch;
      return /^PB-\d{14}$/.test(value || "") ? value : null;
    },
    pendingReviewBatchId() {
      return this.pendingReview ? this.pendingReview.batch_id : null;
    },
    isLinkBatchMismatch() {
      return (
        !!this.linkBatchId && this.linkBatchId !== this.pendingReviewBatchId
      );
    },
    tickableRows() {
      return this.groupedData.filter(row => !this.isPayDisabled(row));
    },
    isAllTicked() {
      return (
        this.tickableRows.length > 0 &&
        this.tickableRows.every(row => this.isTicked(row))
      );
    },
    isIndeterminate() {
      const ticked = this.tickableRows.filter(row => this.isTicked(row)).length;
      return ticked > 0 && ticked < this.tickableRows.length;
    },
    missingMonths() {
      const missing = [];
      const end = moment(this.filter.end_month, "YYYY-MM");
      const now = moment();
      const cursor = moment(this.filter.start_month, "YYYY-MM");
      while (cursor.isSameOrBefore(end, "month")) {
        // future months can not have payouts yet, so they are not reported as missing
        if (
          !cursor.isAfter(now, "month") &&
          !this.monthsWithData.includes(cursor.format("YYYY-MM"))
        ) {
          missing.push(cursor.format("YYYY-MM"));
        }
        cursor.add(1, "month");
      }
      return this.formatMonthsText(missing);
    },
    generateReportLabel() {
      return this.tickedCount > 0
        ? `Generate Report (${this.tickedCount})`
        : "Generate Report";
    },
    wiseBalanceText() {
      if (!this.wiseBalance) {
        return "-";
      }
      return `$${this.formatPrice(
        parseFloat(this.wiseBalance.balance).toFixed(2)
      )}`;
    },
    accumulatedValue() {
      return Object.values(this.tickedArtists).reduce(
        (sum, ticked) => sum + parseFloat(ticked.amount),
        0
      );
    },
    accumulatedValueText() {
      return `$${this.formatPrice(this.accumulatedValue.toFixed(2))}`;
    },
    schedulePeriodText() {
      return this.formatMonthRange(
        this.filter.start_month,
        this.filter.end_month
      );
    },
    balanceAfterText() {
      if (!this.wiseBalance) {
        return "-";
      }
      const after =
        parseFloat(this.wiseBalance.balance) - this.accumulatedValue;
      return `$${this.formatPrice(after.toFixed(2))}`;
    },
    // scheduling is blocked when the ticked artists are worth more than the real Wise balance
    hasInsufficientFunds() {
      if (!this.wiseBalance) {
        return false;
      }
      return this.accumulatedValue > parseFloat(this.wiseBalance.balance);
    },
    // the pending account-owner review batch; null when the table is unlocked
    pendingReview() {
      return this.pendingPayoutReview && this.pendingPayoutReview.data
        ? this.pendingPayoutReview.data
        : null;
    },
    // only the configured account owner may open the review
    canReviewPending() {
      return !!(
        this.pendingPayoutReview && this.pendingPayoutReview.can_review
      );
    },
    pendingReviewArtists() {
      return this.pendingReview ? this.pendingReview.artists : [];
    },
    pendingReviewTotalText() {
      if (!this.pendingReview) {
        return "-";
      }
      return `$${this.formatPrice(
        parseFloat(this.pendingReview.total_amount).toFixed(2)
      )}`;
    },
    pendingReviewCountText() {
      if (!this.pendingReview) {
        return "-";
      }
      const count = this.pendingReview.total_artists;
      return `${count} ${count === 1 ? "artist" : "artists"}`;
    },
    pendingReviewBannerText() {
      if (!this.pendingReview) {
        return "";
      }
      const period = this.formatMonthRange(
        this.pendingReview.start_month,
        this.pendingReview.end_month
      );
      const submitted = moment(this.pendingReview.submitted_at).format(
        "MMM D, YYYY h:mm A"
      );
      return `${this.pendingReviewCountText} · ${this.pendingReviewTotalText} (${period}) submitted ${submitted}. No changes can be made until the account owner approves or rejects.`;
    },
    ownerReviewButtonTooltip() {
      return this.canReviewPending
        ? "Open the payout batch review"
        : "View the pending batch; only the account owner can approve or reject";
    },
    // scheduling is locked while a batch awaits the account owner's review
    isReviewLocked() {
      return !!this.pendingReview;
    }
  },
  watch: {
    listPayout: {
      immediate: true,
      // eslint-disable-next-line no-unused-vars
      async handler(val) {
        if (this.listPayout.data !== undefined) {
          this.groupedData = this.groupByArtist(this.listPayout.data.data);
          this.total = this.groupedData.length;
          this.page = 1;
          this.applyPagedData();
          this.monthsWithData = (val.months || []).map(month => month.month);
        }
      }
    }
  },
  methods: {
    ...mapActions("FinanceModule", [
      "requestListPayout",
      "requestMakePayment",
      "requestSchedulePayout",
      "requestScheduledPayoutHistory",
      "requestPendingPayoutReview",
      "requestPayoutReview",
      "requestWiseBalance"
    ]),
    // the wise_link column stores the artist's Wisetag (e.g. "@elianab210")
    // or a JSON payload with a "wise_handle" key. an artist can only be
    // scheduled when a Wisetag is present; payouts are executed as direct
    // Wise-to-Wise transfers
    hasWiseAccount(row) {
      if (!row.artist || !row.artist.wise_link) {
        return false;
      }
      const wiseLink = String(row.artist.wise_link).trim();
      if (!wiseLink.startsWith("{")) {
        // raw Wisetag, email or phone number identifier
        return wiseLink !== "";
      }
      try {
        const wise = JSON.parse(wiseLink);
        return !!wise && !!wise.wise_handle;
      } catch (err) {
        return false;
      }
    },
    tickDisabledReason(row) {
      if (!this.hasWiseAccount(row)) {
        return "Artist has no Wise account";
      }
      if (row.unpaid_count === 0) {
        return "No unpaid payout";
      }
      if (this.isScheduleLocked(row)) {
        return "Already scheduled";
      }
      return "";
    },
    // the Wise identifier (Wisetag, email or phone number) the scheduler
    // sends the direct Wise-to-Wise payout to
    wiseIdentifierOf(artist) {
      if (!artist || !artist.wise_link) {
        return "";
      }
      const wiseLink = String(artist.wise_link).trim();
      if (!wiseLink.startsWith("{")) {
        return wiseLink;
      }
      try {
        const wise = JSON.parse(wiseLink);
        return (wise && wise.wise_handle) || "";
      } catch (err) {
        return "";
      }
    },
    isTicked(row) {
      return Object.prototype.hasOwnProperty.call(this.tickedArtists, row.id);
    },
    // scheduled payouts are managed through the payment schedule, not through manual pay ticking
    isScheduleLocked(row) {
      return (
        !!row.payment &&
        (row.payment.status === "waiting" ||
          row.payment.status === "being_paid")
      );
    },
    isPayDisabled(row) {
      return (
        row.unpaid_count === 0 ||
        this.isScheduleLocked(row) ||
        !this.hasWiseAccount(row)
      );
    },
    togglePay(row) {
      if (this.isPayDisabled(row)) {
        return;
      }
      if (this.isTicked(row)) {
        this.$delete(this.tickedArtists, row.id);
      } else {
        this.$set(this.tickedArtists, row.id, {
          amount: row.unpaid_amount,
          ids: row.unpaid_items.map(item => item.id)
        });
      }
    },
    onChangeTable(info) {
      this.page = info.page;
      this.pageSize = info.pageSize;
      this.applyPagedData();
    },
    applyPagedData() {
      const start = (this.page - 1) * this.pageSize;
      this.data = this.groupedData.slice(start, start + this.pageSize);
    },
    toggleAll(value) {
      this.tickableRows.forEach(row => {
        if (this.isTicked(row) !== value) {
          this.togglePay(row);
        }
      });
    },
    groupByArtist(rows) {
      const groups = {};
      rows.forEach(pay => {
        if (!Object.prototype.hasOwnProperty.call(groups, pay.user_id)) {
          groups[pay.user_id] = {
            id: pay.user_id,
            artist: pay.artist,
            payment: null,
            created_at: pay.created_at,
            waiting_items: [],
            not_paid_items: [],
            paid_items: []
          };
        }
        const group = groups[pay.user_id];
        if (pay.created_at > group.created_at) {
          group.created_at = pay.created_at;
        }
        if (pay.status === 3) {
          group.paid_items.push(pay);
        } else if (pay.payment) {
          // keep the most recent schedule info of the artist payouts
          if (
            !group.payment ||
            (group.payment.scheduled_at || "") <
              (pay.payment.scheduled_at || "")
          ) {
            group.payment = pay.payment;
          }
          group.waiting_items.push(pay);
        } else {
          group.not_paid_items.push(pay);
        }
      });
      return Object.values(groups)
        .sort((a, b) =>
          a.artist && b.artist ? a.artist.name.localeCompare(b.artist.name) : 0
        )
        .reduce(
          (allRows, group) => allRows.concat(this.buildPayoutRows(group)),
          []
        );
    },
    // every schedule status bucket becomes its own table row, waiting list first
    buildPayoutRows(group) {
      return [
        { id: group.id, pays: group.waiting_items, payment: group.payment },
        {
          id: `${group.id}_notpaid`,
          pays: group.not_paid_items,
          payment: null
        },
        { id: `${group.id}_paid`, pays: group.paid_items, payment: null }
      ]
        .filter(bucket => bucket.pays.length > 0)
        .map(bucket => this.buildPayoutRow(bucket.id, group, bucket));
    },
    buildPayoutRow(id, group, bucket) {
      const row = {
        id,
        artist: group.artist,
        created_at: group.created_at,
        payment: bucket.payment,
        unpaid_items: [],
        unpaid_amount: 0,
        unpaid_months: [],
        paid_months: [],
        paid_amount: 0,
        unpaid_count: 0,
        marked_count: 0
      };
      bucket.pays.forEach(pay => {
        if (pay.status === 3) {
          row.paid_months.push(pay.month);
          row.paid_amount += parseFloat(pay.amount || pay.request_amount);
        } else {
          row.unpaid_items.push({
            id: pay.id,
            request_amount: parseFloat(pay.request_amount)
          });
          row.unpaid_amount += parseFloat(pay.request_amount);
          row.unpaid_months.push(pay.month);
          row.unpaid_count += 1;
          if (pay.status === 1) {
            row.marked_count += 1;
          }
        }
      });
      row.amount_text = `$${this.formatPrice(
        (row.unpaid_count > 0 ? row.unpaid_amount : row.paid_amount).toFixed(2)
      )}`;
      row.months_text = this.formatMonthsText(
        row.unpaid_months.length > 0 ? row.unpaid_months : row.paid_months
      );
      return row;
    },
    async search() {
      await this.requestListPayout(this.filter);
    },
    async onChangeStartDate() {
      await this.applyMonthFilter();
    },
    async onChangeEndDate() {
      await this.applyMonthFilter();
    },
    async applyMonthFilter() {
      const now = moment();
      let start = moment(this.startMonth);
      let end = moment(this.endMonth);
      // the filter can not go beyond the current month
      if (start.isAfter(now, "month")) {
        start = now;
      }
      if (end.isAfter(now, "month")) {
        end = now;
      }
      // from can not be after to
      if (start.isAfter(end, "month")) {
        end = start;
      }
      this.startMonth = start.toDate();
      this.endMonth = end.toDate();
      this.filter = {
        ...this.filter,
        start_month: start.format("YYYY-MM"),
        end_month: end.format("YYYY-MM")
      };
      this.tickedArtists = {};
      await this.search();
      this.restoreTickedPayouts();
    },
    restoreTickedPayouts() {
      this.groupedData
        .filter(
          group =>
            this.hasWiseAccount(group) &&
            !this.isScheduleLocked(group) &&
            group.unpaid_count > 0 &&
            group.marked_count === group.unpaid_count
        )
        .forEach(group =>
          this.$set(this.tickedArtists, group.id, {
            amount: group.unpaid_amount,
            ids: group.unpaid_items.map(item => item.id)
          })
        );
    },
    async refreshWiseBalance() {
      await this.requestWiseBalance();
    },
    saveMarkPay() {
      if (this.tickedCount === 0) {
        return;
      }
      if (this.isReviewLocked) {
        this.$swal(
          "Schedule Locked",
          "A payout batch is awaiting account owner approval. No new payouts can be scheduled until it is approved or rejected.",
          "warning"
        );
        return;
      }
      if (this.hasInsufficientFunds) {
        this.$swal(
          "Insufficient Wise Balance",
          "The accumulated value exceeds the Wise balance. Untick some artists before saving.",
          "warning"
        );
        return;
      }
      this.isShowingConfirmModal = true;
    },
    closeConfirmModal() {
      this.isShowingConfirmModal = false;
    },
    closeSuccessModal() {
      this.isShowingSuccessModal = false;
      this.scheduledSummary = null;
    },
    async confirmSchedulePayout() {
      // snapshot the batch values before scheduling, because the payout list
      // refreshes and the ticked state changes once the schedule is saved
      this.scheduledSummary = {
        artistText: `${this.tickedCount} ${
          this.tickedCount === 1 ? "artist" : "artists"
        }`,
        total: this.accumulatedValueText,
        period: this.schedulePeriodText
      };
      const data = {
        start_month: this.filter.start_month,
        end_month: this.filter.end_month,
        ids: Object.values(this.tickedArtists).reduce(
          (ids, ticked) => ids.concat(ticked.ids),
          []
        )
      };
      const filter = this.filter;
      this.isShowingConfirmModal = false;
      try {
        await this.requestSchedulePayout({ data, filter });
      } catch (err) {
        // the failed request already surfaced its error through the notification store
        return;
      }
      await this.requestScheduledPayoutHistory();
      this.isShowingSuccessModal = true;
    },
    openOwnerReview() {
      this.isShowingOwnerModal = true;
    },
    // review-request email links land on ?review=1; any dashboard admin may
    // open the approval modal, but only the account owner may act on it
    maybeOpenOwnerReviewFromLink() {
      if (this.$route.query.review !== "1") {
        return;
      }
      if (!this.pendingReview) {
        this.$swal(
          "No Pending Batch",
          "This payout batch has already been approved or rejected.",
          "info"
        );
        return;
      }
      if (this.isLinkBatchMismatch) {
        this.$swal(
          "Different Batch",
          `This link is for batch ${this.linkBatchId}, but the batch awaiting review is ${this.pendingReviewBatchId}.`,
          "warning"
        );
      }
      this.isShowingOwnerModal = true;
    },
    closeOwnerReview() {
      this.isShowingOwnerModal = false;
    },
    artistAmountText(artist) {
      return `$${this.formatPrice(parseFloat(artist.amount).toFixed(2))}`;
    },
    async reviewPendingPayout(decision) {
      // only the configured account owners may approve or reject the batch;
      // every other admin can only view it (the API enforces this too)
      if (!this.canReviewPending) {
        return;
      }
      const isApprove = decision === "approved";
      const result = await this.$swal({
        title: isApprove ? "Approve payout batch" : "Reject payout batch",
        text: isApprove
          ? "The Wise transfers for this batch will be executed immediately. Continue?"
          : "The batch will be cancelled and its payouts returned to the unscheduled state. Continue?",
        icon: "warning",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        showCancelButton: true
      });
      if (!result.value) {
        return;
      }
      this.isShowingOwnerModal = false;
      if (isApprove) {
        this.$swal(
          "Payment in Process",
          "The payment is in process. Once successful, you will be notified through email.",
          "info"
        );
        // the transfers are executed on the backend and the result arrives by
        // email, so the approval request keeps running in the background
        this.requestPayoutReview({
          data: { decision },
          filter: this.filter
        }).catch(() => {
          // the failed request already surfaced its error through the notification store
        });
        return;
      }
      try {
        await this.requestPayoutReview({
          data: { decision },
          filter: this.filter
        });
      } catch (err) {
        // the failed request already surfaced its error through the notification store
        return;
      }
      this.$swal(
        "Rejected",
        "The payout batch has been rejected and cancelled.",
        "success"
      );
    },
    historyStatusClass(status) {
      if (status === "Paid") {
        return "bg-green-100 text-green-700";
      }
      if (status === "Cancelled") {
        return "bg-gray-200 text-gray-600";
      }
      if (status === "Failed") {
        return "bg-red-100 text-red-700";
      }
      return "bg-yellow-100 text-yellow-700";
    },
    paymentMethodText(row) {
      if (this.hasWiseAccount(row)) {
        return "Wise";
      }
      if (row.artist && row.artist.bank) {
        return "Bank";
      }
      return "-";
    },
    formatMonth(month) {
      const parsed = moment(month, "YYYY-MM");
      return month && parsed.isValid() ? parsed.format("MMM YYYY") : "-";
    },
    formatMonthsText(months) {
      const ranges = [];
      months
        .map(month => moment(month, "YYYY-MM"))
        .filter(month => month.isValid())
        .sort((a, b) => a.valueOf() - b.valueOf())
        .forEach(current => {
          const last = ranges[ranges.length - 1];
          if (
            last &&
            current.isSame(last.end.clone().add(1, "month"), "month")
          ) {
            last.end = current;
          } else {
            ranges.push({ start: current, end: current });
          }
        });
      return ranges
        .map(range => this.formatMonthRange(range.start, range.end))
        .join(", ");
    },
    formatMonthRange(startMonth, endMonth) {
      const start = moment(startMonth, "YYYY-MM");
      const end = moment(endMonth, "YYYY-MM");
      if (!start.isValid() || !end.isValid()) {
        return "-";
      }
      if (start.isSame(end, "month")) {
        return start.format("MMM YYYY");
      }
      if (start.isSame(end, "year")) {
        return `${start.format("MMM")} – ${end.format("MMM YYYY")}`;
      }
      return `${start.format("MMM YYYY")} – ${end.format("MMM YYYY")}`;
    },
    payoutDisplayStatus(row) {
      if (row.payment) {
        if (row.payment.status === "waiting") {
          return `Waiting - ${moment(row.payment.scheduled_at).format(
            "MMM D, YYYY h:mm A"
          )}`;
        }
        if (row.payment.status === "being_paid") {
          return "Processing";
        }
        if (row.payment.status === "failed") {
          return "Failed";
        }
      }
      if (row.unpaid_count === 0) {
        return "Paid";
      }
      return "NotPaid";
    },
    escapeCsvValue(value) {
      if (value === null || value === undefined) {
        return "";
      }
      const text = String(value);
      if (/[",\n\r]/.test(text)) {
        return `"${text.replace(/"/g, '""')}"`;
      }
      return text;
    },
    generateReport() {
      if (this.tickedCount === 0) {
        return;
      }
      const tickedRows = this.groupedData.filter(row => this.isTicked(row));
      if (tickedRows.length === 0) {
        return;
      }
      const headers = [
        "Artist Name",
        "Email",
        "Bank",
        "Account Holder",
        "Account Number",
        "Swift Code",
        "Amount",
        "Months",
        "Status",
        "Requested At"
      ];
      const rows = tickedRows.map(row => [
        row.artist ? row.artist.name : "",
        row.artist ? row.artist.email : "",
        row.artist && row.artist.bank ? row.artist.bank.bank : "",
        row.artist && row.artist.bank ? row.artist.bank.account_name : "",
        row.artist && row.artist.bank ? row.artist.bank.account_number : "",
        row.artist && row.artist.bank ? row.artist.bank.swift_code : "",
        row.unpaid_amount.toFixed(2),
        row.months_text,
        this.payoutDisplayStatus(row),
        row.created_at
      ]);
      const csv = [headers, ...rows]
        .map(rowValues =>
          rowValues.map(value => this.escapeCsvValue(value)).join(",")
        )
        .join("\n");
      fileDownload(
        new Blob([csv], { type: "text/csv;charset=utf-8;" }),
        `payout_report_${this.filter.start_month}_${this.filter.end_month}.csv`
      );
    },
    resetForm() {
      this.$v.input.$reset();
      this.input = Object.assign(
        {},
        {
          amount: 0,
          payment_date: new Date()
        }
      );
    },
    viewDetail(row) {
      this.resetForm();
      this.detailGroup = row;
      this.artist = {
        ...row.artist,
        amount: row.amount_text,
        created_at: row.created_at
      };
      this.isShowingDetailModal = !this.isShowingDetailModal;
      this.input = {
        amount: row.unpaid_amount.toFixed(2),
        payment_date: new Date()
      };
    },
    async makePayment() {
      this.$v.input.$touch();
      if (this.$v.input.$invalid) {
        return;
      }
      const unpaidItems = this.detailGroup.unpaid_items;
      const paymentDate = moment(this.input.payment_date).format("YYYY-MM-DD");
      const filter = this.filter;
      Vue.swal({
        title: "Submit as Transferred",
        text: `Are you sure to Submit as Transferred with artist : ${this.artist.name} for ${unpaidItems.length} month(s)`,
        icon: "warning",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        showCancelButton: true
      }).then(async result => {
        if (result.value) {
          try {
            // each payout is transferred with its own requested amount, so the balance deduction stays correct;
            // requests run sequentially by chaining them
            await unpaidItems.reduce(
              (promise, item) =>
                promise.then(async () => {
                  const data = {
                    id: item.id,
                    amount: Number(item.request_amount),
                    payment_date: paymentDate
                  };
                  await this.requestMakePayment({ data, filter });
                }),
              Promise.resolve()
            );
            this.$delete(this.tickedArtists, this.detailGroup.id);
            this.isShowingDetailModal = false;
            await this.requestScheduledPayoutHistory();
            this.$swal(
              "Approved",
              "You successfully approve this request",
              "success"
            );
          } catch (err) {
            // the failed request already surfaced its error through the notification store
          }
        }
      });
    }
  },
  components: {
    VueCustomTooltip,
    ModalDialog,
    Card,
    FormItem,
    Input,
    TextError,
    Button
  }
};
</script>



// WEBPACK FOOTER //
// src/views/PayoutDashboard.vue